import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { paymentCatalog, paymentLinks, createCheckout } from "../lib/api";
import { toast } from "sonner";
import {
  ShoppingBag, Link2, Copy, Check, ArrowRight, Loader2, Lock,
  BookOpen, GraduationCap, Users, Mountain, Sparkles, Heart, Gem
} from "lucide-react";

// ─── Product metadata for shop presentation ──────────────────────────
const KIND_META = {
  book: { label: "Buch", icon: BookOpen, accent: "#C9A36A" },
  course: { label: "Kurs", icon: GraduationCap, accent: "#4B2E83" },
  workshop: { label: "Workshop", icon: Users, accent: "#8B7BB8" },
  retreat: { label: "Retreat", icon: Mountain, accent: "#2E5C3E" },
  programme: { label: "Programm", icon: Gem, accent: "#D4AF37" },
  community: { label: "Community · Abo", icon: Heart, accent: "#C94A5C" },
  lifemap: { label: "Lebenslandkarte", icon: Sparkles, accent: "#4B2E83" },
};

// Display order + optional grouping label
const CATEGORY_ORDER = [
  { key: "flagship",  label: "Flagship Programme",   kinds: ["programme"] },
  { key: "learning",  label: "Kurse & Akademie",     kinds: ["course"] },
  { key: "live",      label: "Workshops & Retreats", kinds: ["workshop", "retreat"] },
  { key: "digital",   label: "Digitale Produkte",    kinds: ["book", "lifemap"] },
  { key: "community", label: "Community Abo",        kinds: ["community"] },
];

const formatPrice = (amount, currency) => {
  try {
    return new Intl.NumberFormat("de-CH", { style: "currency", currency: (currency || "chf").toUpperCase(), maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${currency?.toUpperCase() || "CHF"} ${amount}`;
  }
};

// Human-readable modality (Einmalzahlung, Ratenzahlung, Abo)
const modeLabel = (item) => {
  if (item.title?.toLowerCase().includes("3 raten")) return "3 monatliche Raten";
  if (item.title?.toLowerCase().includes("6 raten")) return "6 monatliche Raten";
  if (item.kind === "community") return item.title?.toLowerCase().includes("jährlich") ? "Jährlich · 2 Monate gratis" : "Monatlich kündbar";
  return "Einmalzahlung";
};

const ProductCard = ({ p, linkUrl, onBuy, onCopy, copied, busyKey }) => {
  const meta = KIND_META[p.kind] || { label: p.kind, icon: ShoppingBag, accent: "#D4AF37" };
  const Icon = meta.icon;
  const busy = busyKey === p.product_key;

  // Premium-Gate: Programme (SAT Master/Mentor/Bundle) zeigen keinen Preis,
  // sondern verlangen ein Bewerbungsgespräch.
  const gated = p.kind === "programme";

  return (
    <div
      data-testid={`shop-card-${p.product_key}`}
      className="group relative flex flex-col rounded-2xl border border-white/10 bg-gradient-to-br from-[#0f1a35]/70 to-[#081328]/50 backdrop-blur p-6 transition-all duration-500 hover:border-white/25 hover:-translate-y-1 hover:shadow-[0_24px_80px_-20px_rgba(212,175,55,0.25)]"
    >
      {/* Accent bar */}
      <div
        className="absolute top-0 left-6 right-6 h-[2px] rounded-full opacity-70 group-hover:opacity-100 transition"
        style={{ background: `linear-gradient(90deg, transparent, ${meta.accent}, transparent)` }}
      />

      {/* Kind pill */}
      <div className="flex items-center justify-between mb-5">
        <span
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase px-2.5 py-1 rounded-full border"
          style={{ color: meta.accent, borderColor: `${meta.accent}55` }}
        >
          <Icon size={12} /> {meta.label}
        </span>
        <span className="text-[10px] text-white/40 tracking-wider uppercase">
          {gated ? "Nur nach Erstgespräch" : modeLabel(p)}
        </span>
      </div>

      {/* Title */}
      <h3 className="serif text-xl font-light leading-snug text-white/95 mb-2 min-h-[3.5rem]">
        {p.title}
      </h3>

      {/* Subtitle · optional Beschreibungszeile */}
      {p.subtitle && (
        <p className="text-sm text-white/60 leading-snug mb-3 min-h-[2.5rem]" data-testid={`shop-subtitle-${p.product_key}`}>
          {p.subtitle}
        </p>
      )}

      {/* Price / Gated */}
      <div className="mt-auto mb-5">
        {gated ? (
          <div className="flex items-baseline gap-2">
            <Lock size={16} className="text-[#D4AF37]" />
            <span className="serif text-2xl font-light text-white/90">Investition auf Anfrage</span>
          </div>
        ) : (
          <div className="flex items-baseline gap-2">
            <span className="serif text-3xl font-light" style={{ color: meta.accent }}>
              {formatPrice(p.amount, p.currency)}
            </span>
            {p.kind === "community" && (
              <span className="text-xs text-white/50">/ {p.title?.toLowerCase().includes("jährlich") ? "Jahr" : "Monat"}</span>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      {gated ? (
        <a
          href="/erstgespraech"
          data-testid={`shop-apply-${p.product_key}`}
          className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium tracking-wide transition-all"
          style={{ background: meta.accent, color: "#081328" }}
        >
          <Sparkles size={14} /> Bewerbungsgespräch buchen
        </a>
      ) : (
        <div className="flex gap-2">
          <button
            data-testid={`shop-buy-${p.product_key}`}
            onClick={() => onBuy(p.product_key)}
            disabled={busy}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium tracking-wide transition-all disabled:opacity-60 disabled:cursor-wait"
            style={{ background: meta.accent, color: "#081328" }}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
            {busy ? "Öffnet …" : "Jetzt kaufen"}
          </button>

          <button
            data-testid={`shop-copy-${p.product_key}`}
            onClick={() => onCopy(p.product_key, linkUrl)}
            disabled={!linkUrl}
            title={linkUrl ? "Kauflink kopieren" : "Link wird geladen …"}
            className="inline-flex items-center justify-center rounded-xl px-3 py-3 border border-white/10 text-white/80 hover:text-white hover:border-white/25 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
          </button>
        </div>
      )}

      {!gated && linkUrl && (
        <a
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`shop-link-${p.product_key}`}
          className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-white/40 hover:text-white/70 transition"
        >
          <Link2 size={11} /> Direkter Stripe-Link · in neuem Tab öffnen
        </a>
      )}
    </div>
  );
};

const Shop = () => {
  const [catalog, setCatalog] = useState({});
  const [links, setLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cat, ln] = await Promise.all([
          paymentCatalog(),
          paymentLinks().catch(() => ({ links: {} })), // don't block shop if links fail
        ]);
        if (!alive) return;
        setCatalog(cat || {});
        setLinks(ln?.links || {});
      } catch (e) {
        console.error(e);
        toast.error("Shop konnte nicht geladen werden.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const grouped = useMemo(() => {
    const items = Object.entries(catalog).map(([k, v]) => ({ product_key: k, ...v }));
    const byCat = {};
    for (const cat of CATEGORY_ORDER) byCat[cat.key] = [];

    // Programme: nur EINE Karte pro programme-Familie (statt einer pro Rate/Einmalzahl).
    // Wir bevorzugen die "full"-Variante als Repräsentant, weil sie den vollen Titel trägt.
    const seenProgramme = new Set();

    for (const it of items) {
      const cat = CATEGORY_ORDER.find(c => c.kinds.includes(it.kind));
      if (!cat) continue;

      if (it.kind === "programme") {
        const fam = it.programme || it.product_key;
        if (seenProgramme.has(fam)) continue;
        // Nur Einmalzahlung als Repräsentant (installments === 1)
        if (Number(it.installments) !== 1) {
          const hasFull = items.some(x => x.kind === "programme" && x.programme === fam && Number(x.installments) === 1);
          if (hasFull) continue;
        }
        seenProgramme.add(fam);
        // Titel aufräumen: „ · Einmalzahlung" entfernen, damit die Karte generisch wirkt
        const cleanTitle = (it.title || "").replace(/\s*·\s*Einmalzahlung$/i, "");
        byCat[cat.key].push({ ...it, title: cleanTitle });
      } else {
        byCat[cat.key].push(it);
      }
    }
    // Deterministic ordering: Einmalzahlung first, then Raten
    for (const k of Object.keys(byCat)) {
      byCat[k].sort((a, b) => {
        const aRate = /rate/i.test(a.product_key) ? 1 : 0;
        const bRate = /rate/i.test(b.product_key) ? 1 : 0;
        if (aRate !== bRate) return aRate - bRate;
        return (a.amount || 0) - (b.amount || 0);
      });
    }
    return byCat;
  }, [catalog]);

  const handleBuy = async (product_key) => {
    setBusy(product_key);
    try {
      const { url } = await createCheckout(product_key);
      if (url) window.location.href = url;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Checkout fehlgeschlagen";
      toast.error(msg);
      setBusy(null);
    }
  };

  const handleCopy = async (product_key, url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(product_key);
      toast.success("Kauflink kopiert · bereit zum Teilen");
      setTimeout(() => setCopiedKey(null), 2200);
    } catch {
      toast.error("Kopieren fehlgeschlagen — bitte manuell markieren.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#050b1c] via-[#081328] to-[#050a18] text-white pt-28 pb-24" data-testid="shop-page">
      <Helmet>
        <title>Shop · SAT Akademie</title>
        <meta name="description" content="Bücher, Kurse, Workshops, Retreats und Transformationsprogramme der SAT Akademie. Sichere Bezahlung via Stripe." />
      </Helmet>

      <div className="container-sat max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] mb-6">
            <ShoppingBag size={12} /> SAT Shop
          </span>
          <h1 className="serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] mb-6">
            Alles, was <em className="italic text-[#D4AF37]">Transformation</em>
            <br />
            möglich macht.
          </h1>
          <p className="max-w-2xl mx-auto text-base sm:text-lg text-white/60 leading-relaxed">
            Von einem Buch bis zur 9-monatigen Mentor-Ausbildung. Alles, was du zum Beginnen brauchst — an einem Ort.
            Bezahlung sicher über Stripe · Karte, TWINT, Apple Pay & mehr.
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 text-white/50" data-testid="shop-loading">
            <Loader2 className="animate-spin mb-3" size={28} />
            <span className="text-sm">Lade Produkte …</span>
          </div>
        ) : (
          <div className="space-y-20">
            {CATEGORY_ORDER.map(cat => {
              const items = grouped[cat.key] || [];
              if (!items.length) return null;
              return (
                <section key={cat.key} data-testid={`shop-section-${cat.key}`}>
                  <div className="flex items-center gap-4 mb-8">
                    <span className="serif text-2xl font-light text-white/95">{cat.label}</span>
                    <span className="flex-1 h-px bg-gradient-to-r from-white/15 via-white/5 to-transparent" />
                    <span className="text-xs text-white/40 tracking-wider">{items.length} {items.length === 1 ? "Produkt" : "Produkte"}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map(p => (
                      <ProductCard
                        key={p.product_key}
                        p={p}
                        linkUrl={links[p.product_key]?.url}
                        onBuy={handleBuy}
                        onCopy={handleCopy}
                        copied={copiedKey === p.product_key}
                        busyKey={busy}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {/* Trust footer */}
        <div className="mt-24 border-t border-white/10 pt-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm text-white/60">
          <div>
            <div className="text-white/90 mb-1.5 flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Sichere Zahlung</div>
            <p className="text-xs leading-relaxed">Zahlung über Stripe (SSL, PCI-DSS Level 1). Wir speichern niemals deine Kartendaten.</p>
          </div>
          <div>
            <div className="text-white/90 mb-1.5 flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Ratenzahlung verfügbar</div>
            <p className="text-xs leading-relaxed">Grössere Programme in 3 Raten. Automatischer Einzug — keine Verwaltung nötig.</p>
          </div>
          <div>
            <div className="text-white/90 mb-1.5 flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> 14 Tage Reflexion</div>
            <p className="text-xs leading-relaxed">Bei allen Programmen: 14 Tage Reflexionszeit nach Start · Details in den <a href="/agb" className="underline hover:text-white">AGB</a>.</p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <a
            href="/erstgespraech"
            data-testid="shop-cta-erstgespraech"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#D4AF37] transition"
          >
            Unsicher, was zu dir passt? Buche ein kostenloses Erstgespräch <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default Shop;
